import type { Directive } from 'vue'

const listeners = new WeakMap<HTMLElement, (event: TransitionEvent) => void>()

function hasRowTransition(el: HTMLElement): boolean {
  const style = getComputedStyle(el)
  const properties = style.transitionProperty.split(',').map((name) => name.trim())
  const durations = style.transitionDuration.split(',').map((value) => Number.parseFloat(value))
  return properties.some(
    (name, index) =>
      (name === 'grid-template-rows' || name === 'all') &&
      (durations[index % durations.length] ?? 0) > 0,
  )
}

function apply(el: HTMLElement, open: boolean): void {
  el.dataset.state = open ? 'open' : 'closed'
  delete el.dataset.settled
  if (open && !hasRowTransition(el)) el.dataset.settled = ''
}

/** Drives a `.collapsible` grid from whether it is open, and marks it `data-settled` once fully open so its content stops clipping. */
export const vCollapsible: Directive<HTMLElement, boolean> = {
  mounted(el, binding) {
    el.dataset.state = binding.value ? 'open' : 'closed'
    if (binding.value) el.dataset.settled = ''
    const onEnd = (event: TransitionEvent): void => {
      if (event.target !== el || event.propertyName !== 'grid-template-rows') return
      if (el.dataset.state === 'open') el.dataset.settled = ''
    }
    el.addEventListener('transitionend', onEnd)
    listeners.set(el, onEnd)
  },
  updated(el, binding) {
    if (binding.value === binding.oldValue) return
    apply(el, binding.value)
  },
  beforeUnmount(el) {
    const onEnd = listeners.get(el)
    if (onEnd) el.removeEventListener('transitionend', onEnd)
    listeners.delete(el)
  },
}
